import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../contexts/CartContext';
import { useCurrency } from '../../hooks/useCurrency';

const CartModal = () => {
  const { t, i18n } = useTranslation();
  const { cartItems, removeFromCart, updateQuantity, getTotalPrice, isCartOpen, setIsCartOpen } = useCart();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'fa';

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isCartOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isCartOpen) {
        setIsCartOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isCartOpen, setIsCartOpen]);

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    setIsCartOpen(false);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsCartOpen(false);
    }
  };

  if (!isCartOpen) return null;

  const shippingThreshold = 100;
  const remainingForFreeShipping = Math.max(0, shippingThreshold - getTotalPrice());
  const hasQualifiedForFreeShipping = remainingForFreeShipping === 0;

  return (
    <>
      {/* Backdrop with blur */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isCartOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleBackdropClick}
      />

      {/* Cart Modal - Mobile First Design */}
      <div
        className={`fixed top-0 ${isRTL ? 'left-0' : 'right-0'} h-full w-full md:w-[480px] bg-white shadow-2xl z-50 transform transition-all duration-500 ease-out flex flex-col ${
          isCartOpen ? 'translate-x-0' : isRTL ? '-translate-x-full' : 'translate-x-full'
        }`}
      >
        {/* Header with gradient - Mobile Optimized */}
        <div className="relative bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 text-white p-4 md:p-6 shadow-lg flex-shrink-0">
          {/* Decorative pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
              backgroundSize: '20px 20px',
            }}></div>
          </div>

          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg">
                <i className="ri-shopping-cart-2-line text-xl md:text-2xl"></i>
              </div>
              <div>
                <h2 className="text-lg md:text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t('shoppingCart')}
                </h2>
                <p className="text-xs md:text-sm text-white/90 font-medium">
                  {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsCartOpen(false)}
              className="w-10 h-10 md:w-12 md:h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer group touch-manipulation"
              aria-label="Close cart"
            >
              <i className="ri-close-line text-2xl group-hover:rotate-90 transition-transform duration-300"></i>
            </button>
          </div>

          {/* Free Shipping Progress Bar - Mobile Optimized */}
          {!hasQualifiedForFreeShipping && getTotalPrice() > 0 && (
            <div className="mt-3 md:mt-6 animate-slide-down">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs md:text-sm font-semibold text-white/90">
                  <i className="ri-truck-line mr-1"></i>
                  {formatPrice(remainingForFreeShipping)} away from free shipping!
                </p>
                <span className="text-xs font-bold bg-white/20 px-2 py-1 rounded-lg">
                  {Math.round((getTotalPrice() / shippingThreshold) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-orange-400 rounded-full transition-all duration-700 ease-out shadow-lg"
                  style={{ width: `${Math.min((getTotalPrice() / shippingThreshold) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
          )}

          {hasQualifiedForFreeShipping && (
            <div className="mt-3 md:mt-6 p-3 md:p-4 bg-white/20 backdrop-blur-sm rounded-xl md:rounded-2xl border-2 border-white/30 animate-bounce-in">
              <p className="text-xs md:text-sm font-bold text-white flex items-center gap-2">
                <i className="ri-gift-line text-base md:text-xl"></i>
                Congratulations! You've qualified for FREE shipping! 🎉
              </p>
            </div>
          )}
        </div>

        {/* Cart Items - Mobile Optimized Scrolling */}
        <div className="flex-1 overflow-y-auto p-3 md:p-6 space-y-3">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 md:py-16 animate-fade-in">
              <div className="w-20 h-20 md:w-32 md:h-32 bg-gradient-to-br from-gray-100 to-gray-50 rounded-full flex items-center justify-center mb-4 md:mb-6 shadow-inner">
                <i className="ri-shopping-cart-line text-4xl md:text-6xl text-gray-300"></i>
              </div>
              <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-2 md:mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Your cart is empty
              </h3>
              <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-8 text-center px-4">
                Discover our amazing products and start shopping!
              </p>
              <button
                onClick={handleContinueShopping}
                className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl md:rounded-2xl font-bold transition-all duration-300 shadow-lg hover:shadow-emerald-500/50 hover:scale-105 cursor-pointer flex items-center gap-2 whitespace-nowrap touch-manipulation text-sm md:text-base"
              >
                <i className="ri-shopping-bag-line text-lg md:text-xl"></i>
                Start Shopping
              </button>
            </div>
          ) : (
            <>
              {cartItems.map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  className="group bg-gradient-to-br from-white to-gray-50/50 rounded-xl p-3 md:p-4 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-emerald-200 animate-slide-in-right"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                    {/* Product Image - Mobile Optimized */}
                    <div className="relative flex-shrink-0">
                      <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-50 shadow-md group-hover:shadow-lg transition-shadow duration-300">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                      {item.category && (
                        <div className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[9px] md:text-[10px] font-bold rounded-md shadow-lg">
                          {item.category}
                        </div>
                      )}
                    </div>

                    {/* Product Info - Mobile Optimized */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 text-sm md:text-base mb-1 line-clamp-2 group-hover:text-emerald-600 transition-colors duration-300">
                            {item.name}
                          </h3>
                          <p className="text-base md:text-xl font-bold text-emerald-600">
                            {formatPrice(item.price)}
                          </p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="w-8 h-8 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer group/delete flex-shrink-0 touch-manipulation"
                          aria-label="Remove item"
                        >
                          <i className="ri-delete-bin-line text-base group-hover/delete:animate-wiggle"></i>
                        </button>
                      </div>

                      {/* Quantity Controls - Mobile Optimized */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 bg-gray-100 rounded-lg p-1">
                          <button
                            onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                            className="w-8 h-8 bg-white hover:bg-emerald-600 hover:text-white text-gray-700 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer font-bold shadow-sm touch-manipulation"
                            aria-label="Decrease quantity"
                          >
                            <i className="ri-subtract-line text-sm"></i>
                          </button>
                          <span className="w-8 text-center font-bold text-gray-900 text-sm">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-8 h-8 bg-white hover:bg-emerald-600 hover:text-white text-gray-700 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer font-bold shadow-sm touch-manipulation"
                            aria-label="Increase quantity"
                          >
                            <i className="ri-add-line text-sm"></i>
                          </button>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] md:text-xs text-gray-500 mb-0.5">Subtotal</p>
                          <p className="text-sm md:text-lg font-bold text-gray-900">
                            {formatPrice(item.price * item.quantity)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer with Total and Actions - Mobile Optimized */}
        {cartItems.length > 0 && (
          <div className="border-t-2 border-gray-100 bg-gradient-to-br from-white to-gray-50/50 p-3 md:p-6 space-y-3 shadow-2xl flex-shrink-0">
            {/* Total - Mobile Optimized */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 md:p-5 border-2 border-emerald-200/50 shadow-lg">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : 'flex-row'} mb-2`}>
                <span className="text-sm md:text-base text-gray-600 font-semibold">Subtotal:</span>
                <span className="text-base md:text-xl font-bold text-gray-900">{formatPrice(getTotalPrice())}</span>
              </div>
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : 'flex-row'} pt-2 border-t-2 border-emerald-200/50`}>
                <span className="text-base md:text-lg font-bold text-gray-900">Total:</span>
                <span className="text-xl md:text-3xl font-bold text-emerald-600">{formatPrice(getTotalPrice())}</span>
              </div>
            </div>

            {/* Action Buttons - Mobile Optimized */}
            <div className="space-y-2">
              <button
                onClick={handleCheckout}
                className="w-full px-4 py-3 md:py-4 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 hover:from-emerald-700 hover:via-emerald-600 hover:to-teal-700 text-white rounded-xl font-bold text-sm md:text-lg transition-all duration-300 shadow-xl hover:shadow-emerald-500/50 hover:scale-105 cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap group touch-manipulation"
              >
                <i className="ri-secure-payment-line text-lg md:text-2xl"></i>
                Proceed to Checkout
                <i className="ri-arrow-right-line text-base md:text-xl group-hover:translate-x-2 transition-transform"></i>
              </button>
              
              <button
                onClick={handleContinueShopping}
                className="w-full px-4 py-3 md:py-4 bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-200 hover:border-emerald-500 rounded-xl font-bold text-sm md:text-lg transition-all duration-300 cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap hover:scale-105 touch-manipulation"
              >
                <i className="ri-arrow-left-line text-base md:text-xl"></i>
                Continue Shopping
              </button>
            </div>

            {/* Trust Badges - Mobile Optimized */}
            <div className="flex items-center justify-center gap-3 md:gap-6 pt-3 border-t border-gray-200">
              {[
                { icon: 'ri-shield-check-line', text: 'Secure', color: 'from-emerald-500 to-teal-600' },
                { icon: 'ri-truck-line', text: 'Fast Ship', color: 'from-blue-500 to-cyan-600' },
                { icon: 'ri-arrow-go-back-line', text: 'Returns', color: 'from-purple-500 to-pink-600' }
              ].map((badge, index) => (
                <div key={index} className="flex items-center gap-1.5 group/badge">
                  <div className={`w-7 h-7 md:w-8 md:h-8 bg-gradient-to-br ${badge.color} rounded-lg flex items-center justify-center shadow-md group-hover/badge:scale-110 transition-transform`}>
                    <i className={`${badge.icon} text-white text-xs`}></i>
                  </div>
                  <span className="text-[10px] md:text-xs font-semibold text-gray-600">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CartModal;
